import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Banner, Heading, Spinner } from "@panelgenie/shared-ui";
import { SharedPanelLanding } from "../../components/share/SharedPanelLanding.js";
import { SharedSiteLanding } from "../../components/share/SharedSiteLanding.js";

/**
 * Anonymous share-link landing route.
 *
 * Path: `/share/$token`. Resolves to `buildShareUrl(link, { baseUrl })`
 * — the legend's QR codes encode this URL.
 *
 * The route calls the anonymous `share.resolve` procedure (no Clerk
 * JWT required) and dispatches on the returned `ShareResolveResult`:
 *
 *   - `not-found` — generic "unavailable" copy (no existence-leak)
 *   - `expired` / `revoked` — status copy with the relevant date
 *   - `target-missing` — "no longer available" copy
 *   - `live` — dispatch on `view.kind` to the read-only landing
 *
 * Reachable without authentication: "the token is the capability".
 */
export const Route = createFileRoute("/share/$token")({
  component: ShareLandingPage,
});

function ShareLandingPage() {
  const { token } = Route.useParams();
  const { trpc } = Route.useRouteContext();

  const query = useQuery({
    queryKey: ["share", "resolve", token],
    queryFn: () => trpc.share.resolve.query({ token }),
  });

  if (query.isLoading) {
    return (
      <main className="mx-auto max-w-xl px-4 py-8 flex items-center gap-3 text-fg">
        <Spinner size="md" />
        <p className="m-0">Loading shared view…</p>
      </main>
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <main className="mx-auto max-w-xl px-4 py-8">
        <Banner variant="danger" title="Couldn't load this shared link">
          Refresh to try again.
        </Banner>
      </main>
    );
  }

  const result = query.data;
  switch (result.status) {
    case "not-found":
      return (
        <ShareUnavailable
          heading="Link unavailable"
          body="We couldn't find a panel or site for this link. It may have been revoked or never issued."
        />
      );
    case "expired":
      return (
        <ShareUnavailable
          heading="Link expired"
          body={`This share link expired on ${formatInstant(result.expiredAt)}. Ask the owner to issue a new link.`}
        />
      );
    case "revoked":
      return (
        <ShareUnavailable
          heading="Link revoked"
          body={`This share link was revoked on ${formatInstant(result.revokedAt)} and is no longer accessible.`}
        />
      );
    case "target-missing":
      return (
        <ShareUnavailable
          heading="No longer available"
          body="The panel or site this link points to has been deleted."
        />
      );
    case "live":
      // tRPC's serialization widens `revokedAt?: Instant` from a
      // required field to an optional one (it omits `undefined`
      // values on the wire). The view shape is structurally
      // identical at runtime; cast through `as unknown` because the
      // wire-side optionality does not affect rendering.
      if (result.view.kind === "shared-site-view") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return <SharedSiteLanding view={result.view as any} />;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return <SharedPanelLanding view={result.view as any} />;
  }
}

type ShareUnavailableProps = {
  readonly heading: string;
  readonly body: string;
};

const ShareUnavailable = ({ heading, body }: ShareUnavailableProps) => {
  return (
    <main
      aria-label="share-unavailable"
      className="mx-auto max-w-xl px-4 py-10 text-center"
    >
      <Heading level="h1" className="mt-4 mb-2">
        {heading}
      </Heading>
      <p className="text-base text-mute">{body}</p>
    </main>
  );
};

const formatInstant = (instant: string): string => {
  return instant.slice(0, 10);
};
